/**
 * Multi-file upload manager for warranty claims
 * Handles receipt, damage, and serial number file uploads
 */
class WarrantyClaimsFileManager {
    constructor(validator) {
        this.validator = validator;
        // maxFiles enforces per-section upload limits:
        //   receipt: 1 (replaces existing on new upload)
        //   damage:  4 (appends up to limit, ignores extras)
        //   serial:  1 (replaces existing on new upload)
        this.fileUploads = {
            receipt: { files: [], element: null, listElement: null, dropZone: null, maxFiles: 1 },
            damage: { files: [], element: null, listElement: null, dropZone: null, maxFiles: 4 },
            serial: { files: [], element: null, listElement: null, dropZone: null, maxFiles: 1 }
        };
    }

    initialize() {
        this.initializeElements();
        this.attachEventListeners();
    }

    initializeElements() {
        Object.keys(this.fileUploads).forEach(type => {
            this.fileUploads[type].element = document.getElementById(`${type}-files`);
            this.fileUploads[type].listElement = document.getElementById(`${type}-file-list`);
            this.fileUploads[type].dropZone = document.getElementById(`${type}-drop-zone`);
        });
    }

    attachEventListeners() {
        // Browse file triggers
        const browseFilesTriggers = document.querySelectorAll('.browse-files');
        browseFilesTriggers.forEach(trigger => {
            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                const fileType = trigger.dataset.fileType;
                const fileInput = this.fileUploads[fileType].element;
                if (fileInput) {
                    fileInput.click();
                }
            });
        });

        // File input change handlers
        Object.keys(this.fileUploads).forEach(type => {
            const fileInput = this.fileUploads[type].element;
            if (fileInput) {
                fileInput.addEventListener('change', (e) => {
                    this.addFiles(e.target.files, fileInput, type);
                });
            }
        });

        // Drag and drop handlers
        Object.keys(this.fileUploads).forEach(type => {
            this.initializeDropZone(type);
        });
    }

    initializeDropZone(fileType) {
        const dropZone = this.fileUploads[fileType].dropZone;
        if (!dropZone) return;

        ['dragenter', 'dragover'].forEach(evt => {
            dropZone.addEventListener(evt, (e) => {
                e.preventDefault();
                e.stopPropagation();
                dropZone.classList.add('dragover');
            });
        });

        ['dragleave', 'dragend', 'drop'].forEach(evt => {
            dropZone.addEventListener(evt, (e) => {
                e.preventDefault();
                e.stopPropagation();
                dropZone.classList.remove('dragover');
            });
        });

        dropZone.addEventListener('drop', (e) => {
            const dt = e.dataTransfer;
            if (dt && dt.files) {
                this.addFiles(dt.files, this.fileUploads[fileType].element, fileType);
            }
        });
    }

    addFiles(newFiles, fileInput, fileType) {
        const filesArray = Array.from(newFiles || []);
        if (filesArray.length === 0) return;

        const data = this.fileUploads[fileType];
        const maxFiles = data.maxFiles || 1;
        let errorMessage = null;

        if (maxFiles === 1) {
            // Single-file slot: any new upload replaces the previous one.
            const file = filesArray[0];
            if (!this.validator.ALLOWED_MIME_TYPES.includes(file.type)) {
                errorMessage = 'Only JPG, JPEG, PNG or PDF files are allowed.';
            } else if (file.size > this.validator.MAX_FILE_SIZE_BYTES) {
                errorMessage = 'File size must be 30 MB or less.';
            } else {
                // A replacement happens either when there is already a file
                // selected or when the customer picks/drops more than one at once.
                const limitReached = data.files.length > 0 || filesArray.length > 1;
                data.files = [file];
                if (limitReached) {
                    errorMessage = 'File limit reached. Only 1 file is allowed — the previous upload has been replaced with the new one.';
                }
            }
        } else {
            // Multi-file slot: append up to maxFiles, skipping invalid/duplicate files.
            let limitReached = false;
            for (const file of filesArray) {
                if (data.files.length >= maxFiles) {
                    limitReached = true;
                    break;
                }
                if (!this.validator.ALLOWED_MIME_TYPES.includes(file.type)) {
                    errorMessage = 'Only JPG, JPEG, PNG or PDF files are allowed.';
                    continue;
                }
                if (file.size > this.validator.MAX_FILE_SIZE_BYTES) {
                    errorMessage = 'File size must be 30 MB or less.';
                    continue;
                }

                const exists = data.files.some(
                    sf => sf.name === file.name && sf.size === file.size
                );
                if (!exists) {
                    data.files.push(file);
                }
            }

            // Limit message wins over any earlier per-file validation message
            // so the customer always sees why their extra files were dropped.
            if (limitReached) {
                errorMessage = `File limit reached. You can upload up to ${maxFiles} files.`;
            }
        }

        this.renderFileList(fileType);
        this.updateFileInputFiles(fileType);

        if (fileInput) {
            if (errorMessage) {
                this.validator.showFieldError(fileInput, errorMessage);
            } else {
                this.validator.clearFieldError(fileInput);
            }
        }
    }

    renderFileList(fileType) {
        const fileData = this.fileUploads[fileType];
        if (!fileData.listElement) return;

        fileData.listElement.innerHTML = '';
        if (!fileData.files.length) return;

        fileData.files.forEach((file, index) => {
            const listItem = this.createFileListItem(file, index, fileType);
            fileData.listElement.appendChild(listItem);
        });
    }

    createFileListItem(file, index, fileType) {
        const li = document.createElement('li');

        // Optional image thumbnail
        if (file.type && file.type.startsWith('image/')) {
            const thumb = document.createElement('div');
            thumb.className = 'file-thumb';
            const img = document.createElement('img');
            img.alt = file.name;
            try {
                img.src = URL.createObjectURL(file);
                img.onload = () => URL.revokeObjectURL(img.src);
            } catch (err) {
                console.error('Error creating object URL:', err);
            }
            thumb.appendChild(img);
            li.appendChild(thumb);
        }

        const meta = document.createElement('span');
        meta.className = 'file-meta';
        meta.textContent = `${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`;

        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'remove-file';
        removeBtn.textContent = 'Remove';
        removeBtn.addEventListener('click', () => {
            this.removeFile(index, fileType);
        });

        li.appendChild(meta);
        li.appendChild(removeBtn);

        return li;
    }

    removeFile(index, fileType) {
        const fileData = this.fileUploads[fileType];
        fileData.files.splice(index, 1);
        this.renderFileList(fileType);
        this.updateFileInputFiles(fileType);

        // Re-validate the field after removal
        if (fileData.element) {
            const message = this.validator.validateField(fileData.element);
            if (message) {
                this.validator.showFieldError(fileData.element, message);
            } else {
                this.validator.clearFieldError(fileData.element);
            }
        }
    }

    updateFileInputFiles(fileType) {
        const fileData = this.fileUploads[fileType];
        if (!fileData.element) return;

        // Create a new DataTransfer object
        const dt = new DataTransfer();

        // Add all files from our array to the DataTransfer
        fileData.files.forEach(file => {
            dt.items.add(file);
        });

        // Update the file input's files property
        fileData.element.files = dt.files;
    }

    getFiles(fileType) {
        return this.fileUploads[fileType].files;
    }
}

